import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { PopoverDirective } from '@epicuro-next/ataraxia/popover';
import { BehaviorSubject } from 'rxjs';

import { ConnectPermissionsService } from '../../services/connect-permissions.service';

type DeviceType = Record<MediaDeviceKind, MediaDeviceInfo[]>;

@Component({
  selector: 'epc-connect-settings',
  templateUrl: './connect-settings.component.html',
  styleUrls: ['./connect-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ConnectSettingsComponent implements OnInit {
  @ViewChild('inputPopover', { read: PopoverDirective })
  public inputPopoverRef: PopoverDirective;

  @ViewChild('outputPopover', { read: PopoverDirective })
  public outputPopoverRef: PopoverDirective;

  @ViewChild('videoPopover', { read: PopoverDirective })
  public videoPopoverRef: PopoverDirective;

  @Output()
  public audioInputChange = new EventEmitter();

  @Output()
  public videoInputChange = new EventEmitter();

  @Output()
  public audioOutputChange = new EventEmitter();

  public inputDevices$ = new BehaviorSubject<MediaDeviceInfo[]>([]);
  public outputdevices$ = new BehaviorSubject<MediaDeviceInfo[]>([]);
  public videoDevices$ = new BehaviorSubject<MediaDeviceInfo[]>([]);
  public currentInputDevice: MediaDeviceInfo;
  public currentOutputDevice: MediaDeviceInfo;
  public currentVideoDevice: MediaDeviceInfo;
  public canChangeOutput = 'chrome' in window;

  constructor(
    private connectPermissionServices: ConnectPermissionsService,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.setupDevices();
    navigator.mediaDevices.ondevicechange = this.setupDevices.bind(this);
  }

  public selectDevice(device: MediaDeviceInfo, kind: MediaDeviceKind) {
    if (kind === 'audioinput') {
      this.audioInputChange.emit(device.deviceId);
      this.currentInputDevice = device;
      this.inputPopoverRef.close();
    } else if (kind === 'videoinput') {
      this.videoInputChange.emit(device.deviceId);
      this.currentVideoDevice = device;
      this.videoPopoverRef.close();
    } else {
      this.audioOutputChange.emit(device.deviceId);
      this.currentOutputDevice = device;
      this.outputPopoverRef.close();
    }
  }

  private async setupDevices() {
    const devices: DeviceType = {
      audioinput: [],
      audiooutput: [],
      videoinput: [],
    };
    const mediaDevicesInfo = await this.connectPermissionServices.getDevices();
    mediaDevicesInfo.forEach((mediaDevice) => {
      devices[mediaDevice.kind].push(mediaDevice);
    });
    this.inputDevices$.next(devices.audioinput);
    this.outputdevices$.next(devices.audiooutput);
    this.videoDevices$.next(devices.videoinput);
    this.currentInputDevice = devices.audioinput[0];
    this.currentOutputDevice = devices.audiooutput[0];
    this.currentVideoDevice = devices.videoinput[0];
    this.cdr.detectChanges();
  }
}
